import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Building2, Pencil, Plus, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import Navbar from "@/components/Navbar";

const EMPTY = { id: "", nama_kanim: "", kanwil: "", alamat: "", telepon: "", faksimile: "", kota: "" };

export default function AdminKanim() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/kanim");
      setItems(data.items || []);
    } catch {
      toast.error("Gagal memuat data kanim");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (k) => { setEditing(k.id); setForm({ ...k }); setDialogOpen(true); };

  const save = async () => {
    if (!form.nama_kanim || !form.kanwil || !form.kota || !form.alamat) {
      toast.error("Nama kanim, kanwil, alamat, dan kota wajib diisi");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/kanim/${editing}`, form);
        toast.success("Kanim berhasil diperbarui");
      } else {
        await api.post("/admin/kanim", form);
        toast.success("Kanim baru berhasil ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/kanim/${id}`);
      toast.success("Kanim dihapus");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="min-h-screen bg-slate-50/60">
      <Navbar variant="admin" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Kembali ke Dashboard
          </Link>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-600 font-semibold">
                <Building2 className="w-3.5 h-3.5" /> Kelola Kantor Imigrasi
              </div>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">Daftar Kanim</h1>
              <p className="mt-1 text-slate-600">Tambah atau ubah kantor imigrasi — kop surat PDF akan mengikuti data ini.</p>
            </div>
            <Button onClick={openCreate} className="btn-lift rounded-lg bg-blue-600 hover:bg-blue-700" data-testid="kanim-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Tambah Kanim
            </Button>
          </div>

          <div className="mt-8 rounded-2xl bg-white border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-12 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Nama Kanim</TableHead>
                    <TableHead>Kanwil</TableHead>
                    <TableHead>Kota</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10">Belum ada kanim.</TableCell></TableRow>
                  )}
                  {items.map((k) => (
                    <TableRow key={k.id} data-testid={`kanim-row-${k.id}`}>
                      <TableCell><Badge variant="outline" className="font-mono text-xs">{k.id}</Badge></TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-900 text-sm">{k.nama_kanim}</div>
                        <div className="text-xs text-slate-500">{k.alamat}</div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{k.kanwil}</TableCell>
                      <TableCell className="text-sm text-slate-600">{k.kota}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(k)} data-testid={`kanim-edit-${k.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" data-testid={`kanim-delete-${k.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus kanim?</AlertDialogTitle>
                              <AlertDialogDescription><b>{k.nama_kanim}</b> akan dihapus dari daftar pilihan formulir.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={() => del(k.id)} className="bg-red-500 hover:bg-red-600">Hapus</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </motion.div>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="kanim-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Ubah Kantor Imigrasi" : "Tambah Kantor Imigrasi"}</DialogTitle>
            <DialogDescription>Data ini akan tampil pada kop surat PDF. Untuk kanim baru, ID dibuat otomatis dari nama kota jika dikosongkan.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label htmlFor="nama_kanim">Nama Kanim <span className="text-red-500">*</span></Label>
              <Input id="nama_kanim" value={form.nama_kanim} onChange={(e) => set("nama_kanim", e.target.value.toUpperCase())} placeholder="KANTOR IMIGRASI KELAS II TPI BELU" className="mt-1.5" data-testid="kanim-field-nama" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="kanwil">Kantor Wilayah <span className="text-red-500">*</span></Label>
              <Input id="kanwil" value={form.kanwil} onChange={(e) => set("kanwil", e.target.value.toUpperCase())} placeholder="KANTOR WILAYAH NUSA TENGGARA TIMUR" className="mt-1.5" data-testid="kanim-field-kanwil" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="alamat">Alamat <span className="text-red-500">*</span></Label>
              <Input id="alamat" value={form.alamat} onChange={(e) => set("alamat", e.target.value)} placeholder="Jalan Adi Sucipto Nomor 8 Atambua" className="mt-1.5" data-testid="kanim-field-alamat" />
            </div>
            <div>
              <Label htmlFor="telepon">Telepon</Label>
              <Input id="telepon" value={form.telepon} onChange={(e) => set("telepon", e.target.value)} placeholder="(0389) 2325064" className="mt-1.5" data-testid="kanim-field-telepon" />
            </div>
            <div>
              <Label htmlFor="faksimile">Faksimile</Label>
              <Input id="faksimile" value={form.faksimile} onChange={(e) => set("faksimile", e.target.value)} placeholder="(0389) 2325068" className="mt-1.5" data-testid="kanim-field-faksimile" />
            </div>
            <div>
              <Label htmlFor="kota">Kota Tanda Tangan <span className="text-red-500">*</span></Label>
              <Input id="kota" value={form.kota} onChange={(e) => set("kota", e.target.value)} placeholder="Atambua" className="mt-1.5" data-testid="kanim-field-kota" />
            </div>
            {!editing && (
              <div>
                <Label htmlFor="id">ID (opsional)</Label>
                <Input id="id" value={form.id} onChange={(e) => set("id", e.target.value)} placeholder="belu" className="mt-1.5 font-mono" data-testid="kanim-field-id" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Batal</Button>
            <Button onClick={save} disabled={saving} className="btn-lift bg-blue-600 hover:bg-blue-700" data-testid="kanim-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
